// TODO: Convert to typescript
import Snowplow from 'components/analytics/conversions/snowplow';
import { parseBoolean, getCurrentPageParam, featureEnabled } from 'lib/helpers';
import { getAnalyticsDimensions, getPageviewGuid } from 'components/analytics/conversions/tracking';

export const USER_STATE_SCHEMA = 'iglu:com.eezy/user_state/jsonschema/1-0-5';
export const RESOURCE_IMPRESSIONS_SCHEMA = 'iglu:com.eezy/resource_impressions/jsonschema/1-0-0';
export const TURBOLINKS_TIMING_SCHEMA = 'iglu:com.eezy/turbolinks_timing/jsonschema/1-0-0';
export const WEB_VITALS_SCHEMA = 'iglu:com.eezy/web_vitals/jsonschema/1-0-1';

export default function SnowplowTracker({ collector, appId }) {
  const getResourceImpressionsData = () => {
    const data = {
      ids: [],
      pros: [],
      page: 0,
    };
    const page = getCurrentPageParam();

    document.querySelectorAll('[data-item-id]').forEach((resourceEl) => {
      const { itemId, pro } = resourceEl.dataset;

      data.ids.push(parseInt(itemId, 10));

      // use {0,1} instead of booleans to save space in the JSON
      if (parseBoolean(pro)) {
        data.pros.push(1);
      } else {
        data.pros.push(0);
      }
    });

    if (page) {
      data.page = parseInt(page, 10);
    } else {
      delete data.page;
    }

    return data;
  };

  const getUserState = ({ includeFlags = false, includeExpandedDataset = false, errorText = null } = {}) => {
    const {
      user_status_start: start,
      user_status_updated: updated,
      feature_flags: flags,
      split_test: splitTest,
      sign_in_type: type,
      user_auth_id: userAuthId,
    } = getAnalyticsDimensions();

    const [splitTestName, splitTestVariant] = splitTest.split(':');
    const pageviewGuid = getPageviewGuid();

    const payload = { pageviewGuid };

    if (includeFlags) {
      payload.flags = flags;
    }

    if (errorText) {
      payload.errorText = errorText;
    }

    if (includeExpandedDataset) {
      payload.statusStart = start;
      payload.statusUpdated = updated;
      payload.splitTestName = splitTestName;
      payload.splitTestVariant = splitTestVariant;
      payload.signInType = type;
      payload.userAuthId = userAuthId;
    }

    return [{
      schema: USER_STATE_SCHEMA,
      data: payload,
    }];
  };

  const fireResourceImpressionsEvent = () => {
    const data = getResourceImpressionsData();

    if (data.ids.length > 0) {
      this.sp('trackSelfDescribingEvent', {
        schema: RESOURCE_IMPRESSIONS_SCHEMA,
        data,
      },
      getUserState());
    }
  };

  // eslint-disable-next-line arrow-body-style
  const isValidTurbolinkVisit = () => {
    return !Number.isNaN(window.EezyTurbolinksVisits) && parseInt(window.EezyTurbolinksVisits, 10) >= 1;
  };

  const collectTurbolinksTiming = (event) => {
    if (!isValidTurbolinkVisit()) return false;

    const turbolinksTimingEvent = {
      visitDur: event.data.timing.visitEnd - event.data.timing.visitStart,
      reqDur: event.data.timing.requestEnd - event.data.timing.requestStart,
      prevUrl: window.EezyTurbolinksPreviousUrl,
      visitNo: window.EezyTurbolinksVisits,
    };

    this.sp('trackSelfDescribingEvent', {
      schema: TURBOLINKS_TIMING_SCHEMA,
      data: turbolinksTimingEvent,
    },
    getUserState());

    return true;
  };

  const loadListener = (event) => {
    this.trackPageView();
    fireResourceImpressionsEvent();
    collectTurbolinksTiming(event);
  };

  const isValidWebVitalsReport = (vitalsEvent) => {
    if (window.EezyTurbolinksVisits > 0) return false;
    if (vitalsEvent.detail.isFinal === false && vitalsEvent.detail.name !== 'CLS') return false;
    if (vitalsEvent.detail.name === 'CLS' && vitalsEvent.detail.value === 0) return false;

    return true;
  };

  const shiftCLSvalue = (clsScore) => Math.trunc(clsScore * 100);

  const nodeElementToString = (element) => {
    if (!element) return '';

    const parent = element.parentNode;
    let tagString = `${element.tagName}`;

    if (element.classList) {
      tagString += `.${element.classList.toString()}`;
    }

    // only get the parent if it has classes, otherwise it won't tell us much
    if (element.parentNode && element.parentNode.classList && element.parentNode.classList.toString() !== '') {
      tagString = `${parent.tagName}.${parent.classList.toString()} > ${tagString}`;
    }

    return tagString.substring(0, 255);
  };

  const collectCLSSources = (entry) => {
    const sourceNodes = [];
    entry.sources.forEach((source) => {
      sourceNodes.push(nodeElementToString(source.node));
    });

    return sourceNodes;
  };

  const transformWebVitalsReport = (eventDetail) => {
    const transformedWebVitalsReport = {};

    transformedWebVitalsReport.name = eventDetail.name;
    transformedWebVitalsReport.val = (transformedWebVitalsReport.name !== 'CLS')
      ? Math.trunc(eventDetail.value)
      : shiftCLSvalue(eventDetail.value);
    transformedWebVitalsReport.entries = [];

    // eslint-disable-next-line default-case
    switch (transformedWebVitalsReport.name) {
      case 'LCP':
        eventDetail.entries.forEach((entry) => {
          const newEntry = {};

          newEntry.val = Math.trunc(entry.size);
          newEntry.url = entry.url.substring(0, 255);
          newEntry.el = nodeElementToString(entry.element);
          newEntry.load = Math.trunc(entry.loadTime);
          newEntry.sTime = Math.trunc(entry.startTime);
          newEntry.render = Math.trunc(entry.renderTime);

          transformedWebVitalsReport.entries.push(newEntry);
        });
        break;

      case 'FCP':
        eventDetail.entries.forEach((entry) => {
          const newEntry = {};

          newEntry.name = entry.name;
          newEntry.dur = Math.trunc(entry.duration);
          newEntry.sTime = Math.trunc(entry.startTime);
          newEntry.sProcess = Math.trunc(entry.processingStart);
          newEntry.eProcess = Math.trunc(entry.processingEnd);

          transformedWebVitalsReport.entries.push(newEntry);
        });
        break;

      case 'CLS':
        eventDetail.entries.forEach((entry) => {
          const newEntry = {};

          newEntry.dur = Math.trunc(entry.duration);
          newEntry.sTime = Math.trunc(entry.startTime);
          newEntry.recentIn = entry.hadRecentInput;
          newEntry.lastIn = Math.trunc(entry.lastInputTime);
          newEntry.val = shiftCLSvalue(entry.value);

          // Collect element names from the CLS event
          newEntry.els = [];
          if (featureEnabled('web-vitals-cls-sources')) {
            newEntry.els = collectCLSSources(entry);
          }

          transformedWebVitalsReport.entries.push(newEntry);
        });
        break;
    }

    return transformedWebVitalsReport;
  };

  const trackWebVitalsReport = (event) => {
    if (!isValidWebVitalsReport(event)) return;

    const transformedWebVitalsReport = transformWebVitalsReport(event.detail);

    this.sp('trackSelfDescribingEvent', {
      schema: WEB_VITALS_SCHEMA,
      data: transformedWebVitalsReport,
    },
    getUserState());
  };

  this.trackABDetection = () => {
    this.sp('trackStructEvent',
      'ab_detection',
      window.aBDetected,
      '',
      null,
      null,
      getUserState());
  };

  this.trackEvent = ({
    category,
    action,
    label = null,
    value = null,
    contentType = null,
    errorText = null,
  }) => {
    this.sp('trackStructEvent',
      category,
      action,
      label,
      contentType,
      value,
      getUserState({ errorText }));
  };

  this.trackPageView = () => {
    this.sp('trackPageView', null, [], () => getUserState({ includeFlags: true, includeExpandedDataset: true }));
  };

  this.init = () => {
    this.sp = Snowplow;
    this.sp('newTracker', 'cf', collector, {
      appId,
      discoverRootDomain: true,
      eventMethod: 'beacon',
      contexts: {
        webPage: true,
        performanceTiming: true,
        gaCookies: true,
        geolocation: false,
      },
    });

    this.sp('setUserIdFromCookie', 'session_guid');
    this.sp('enableActivityTracking', 10, 10);
    this.sp('enableLinkClickTracking');

    window.addEventListener('turbolinks:load', loadListener);

    if (!featureEnabled('web-vitals-snowplow')) return;
    window.addEventListener('snowplow:web_vitals_report', trackWebVitalsReport);
  };

  this.disconnect = () => {
    window.removeEventListener('turbolinks:load', loadListener);
    window.removeEventListener('snowplow:web_vitals_report', trackWebVitalsReport);
  };
}
